
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PlanTreatment } from '@/types/treatmentPlan';

export type DentalNotationSystem = 'fdi' | 'universal';

interface UseCreateTreatmentPlanReturn {
  title: string;
  description: string;
  treatments: PlanTreatment[];
  selectedTeeth: number[];
  isSubmitting: boolean;
  notationSystem: DentalNotationSystem;
  setTitle: (title: string) => void;
  setDescription: (description: string) => void;
  setNotationSystem: (system: DentalNotationSystem) => void;
  addTreatment: (treatment: PlanTreatment) => void;
  removeTreatment: (index: number) => void;
  toggleToothSelection: (toothId: number) => void;
  calculateTotal: () => number;
  submitPlan: (status: 'draft' | 'sent', patientId: string) => Promise<string | null>;
  resetForm: () => void;
}

export const useCreateTreatmentPlan = (): UseCreateTreatmentPlanReturn => {
  const [title, setTitle] = useState<string>('Dental Treatment Plan');
  const [description, setDescription] = useState<string>('');
  const [treatments, setTreatments] = useState<PlanTreatment[]>([]);
  const [selectedTeeth, setSelectedTeeth] = useState<number[]>([]);
  const [notationSystem, setNotationSystem] = useState<DentalNotationSystem>('fdi');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addTreatment = (treatment: PlanTreatment) => {
    setTreatments([...treatments, treatment]);
  };

  const removeTreatment = (index: number) => {
    setTreatments(treatments.filter((_, i) => i !== index));
  };

  const toggleToothSelection = (toothId: number) => {
    setSelectedTeeth(prev => 
      prev.includes(toothId) 
        ? prev.filter(id => id !== toothId) 
        : [...prev, toothId]
    );
  };

  const calculateTotal = () => {
    return treatments.reduce((sum, treatment) => sum + treatment.price, 0);
  };

  const submitPlan = async (status: 'draft' | 'sent', patientId: string): Promise<string | null> => {
    if (treatments.length === 0) {
      toast.error('Please add at least one treatment to the plan');
      return null;
    }
    
    try {
      setIsSubmitting(true);
      
      // Get current doctor ID (in a real app, would come from auth context)
      const doctorId = 'current-doctor-id'; // This should be replaced with actual doctor ID
      
      // Create treatment plan
      const { data: planData, error: planError } = await supabase
        .from('treatment_plans')
        .insert({
          patient_id: patientId,
          doctor_id: doctorId,
          title: title,
          description: description,
          estimated_cost: calculateTotal(),
          status: status,
          treatment_type: 'dental'
        })
        .select('id')
        .single();
      
      if (planError) {
        throw planError;
      }
      
      if (!planData) {
        throw new Error('Failed to create treatment plan');
      }
      
      // In a real implementation, you would also create treatment_plan_items 
      // in your database for each treatment in the treatments array
      
      toast.success(
        status === 'draft' 
          ? 'Treatment plan saved as draft' 
          : 'Treatment plan submitted to patient'
      );
      
      return planData.id;
      
    } catch (error: any) {
      console.error('Error creating treatment plan:', error);
      toast.error('Failed to create treatment plan: ' + error.message);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTitle('Dental Treatment Plan');
    setDescription('');
    setTreatments([]);
    setSelectedTeeth([]);
    setNotationSystem('fdi');
  };

  return {
    title,
    description,
    treatments,
    selectedTeeth,
    isSubmitting,
    notationSystem,
    setTitle,
    setDescription,
    setNotationSystem,
    addTreatment,
    removeTreatment,
    toggleToothSelection,
    calculateTotal,
    submitPlan,
    resetForm
  };
};

export default useCreateTreatmentPlan;
